import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntity, setAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { buffer, catchError, debounceTime, defaultIfEmpty, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
import { filterConfigMap, FilterControl } from '../components/filters/filters-config';
import { ConnectionType } from '../enums/ConnectionType';
import { TherapyJobStatus } from '../enums/TherapyJobStatus';
import { Filter, FilterQuery } from '../models/Filter';
import { Employee } from '../models/generated/employee';
import { Patient } from '../models/generated/patient';
import { TherapyJobV2 } from '../models/TherapyJobV2';
import { TherapyJobService } from '../services/therapy-job.service';
import { getIdFromText } from '../utils/getIdFromText';
import { LoadInput, resolveShowLoader, runCallbacks } from '../utils/loadInput';
import { getPlannedTimeQuery, getPlannedTimeRange, PlannedTimeRange } from '../utils/plannedTimeRange';
import { splitWords } from '../utils/splitWords';
import { NotificationsStore } from './notification.store';
import { UiStore } from './ui.store';

export const LOAD_DEBOUNCE_TIME = 500;

interface JobsState {
  loading: boolean;
  filter: Filter;
  dirtyAnnotationIds: Array<string>;
  connectionType: ConnectionType | null;
  lastUpdated: number | null;
  processingTherapyJob: string | null;
}

const initialListState: JobsState = {
  loading: null,
  filter: { queries: [] },
  dirtyAnnotationIds: [],
  connectionType: null,
  lastUpdated: null,
  processingTherapyJob: null
};

const loaded: Subject<void> = new Subject<void>();

// A date range control holds a [from, to] array but represents a single applied filter,
// unlike multi-select controls where each selected value is its own filter.
const countAppliedFilters = (f: FilterQuery): number => {
  if (filterConfigMap[f.on]?.control === FilterControl.DATE) {
    return 1;
  }

  return Array.isArray(f.value) ? f.value.length : 1;
};

export const TherapyJobStore = signalStore(
  { providedIn: 'root' },
  withEntities<TherapyJobV2>(),
  withDevtools('jobs'),
  withState(initialListState),
  withMethods((store, jobService = inject(TherapyJobService), notificationStore = inject(NotificationsStore)) => {
    // A reload replaces the whole entity set from a snapshot taken when the request started. Any WS
    // event applied while that snapshot is in flight would otherwise be silently overwritten once the
    // older snapshot lands, so such events are queued here. The notifications are then rebuilt from
    // the same snapshot, which would undo a WS event applied in between as well, so the queue is only
    // replayed once the notifications of the reload have loaded too.
    let loadInFlight = false;
    let pendingWsMutations: Array<() => void> = [];
    // Only the notifications of the latest reload may release the queue, a newer reload keeps it.
    let loadGeneration = 0;

    const releasePendingWsMutations = () => {
      loadInFlight = false;
      const mutations = pendingWsMutations;
      pendingWsMutations = [];
      mutations.forEach((mutate) => mutate());
    };

    // Debounce the requests but collect them in a buffer so no callback is lost when several
    // loads fire in a short time. rxMethod owns the subscription lifecycle (auto teardown).
    const loadJobs = rxMethod<LoadInput>((source$) =>
      source$.pipe(
        buffer(source$.pipe(debounceTime(LOAD_DEBOUNCE_TIME))),
        tap((inputs) => {
          loadInFlight = true;
          loadGeneration++;
          patchState(store, { loading: resolveShowLoader(inputs) });
        }),
        switchMap((inputs) => {
          const range = getPlannedTimeRange(store.filter());
          return jobService.getList(range.from, range.to).then((list) => ({ inputs, list }));
        }),
        tap(({ inputs, list }) => {
          patchState(store, setAllEntities(list));
          // A reload brings fresh server truth, so any pending annotation edits are discarded.
          patchState(store, { loading: false, dirtyAnnotationIds: [], lastUpdated: Date.now() });

          const generation = loadGeneration;
          notificationStore
            .init(store.entities())
            .pipe(
              // Without notifications the held back WS events still have to be applied.
              catchError((error) => {
                console.error('Failed to load the notifications of the therapy job reload', error);
                return of(undefined);
              }),
              take(1),
              defaultIfEmpty(undefined)
            )
            .subscribe(() => {
              if (generation === loadGeneration) {
                releasePendingWsMutations();
              }
            });

          runCallbacks(inputs);

          loaded.next();
        })
      )
    );

    // Removes the dirty annotation flag for jobs whose server data was just refreshed.
    const clearDirtyForIds = (ids: Array<string | undefined>) => {
      const idSet = new Set(ids);
      patchState(store, {
        dirtyAnnotationIds: store.dirtyAnnotationIds().filter((id) => !idSet.has(id))
      });
    };

    const doPatchJob = (value: Partial<TherapyJobV2>, jobId: string) => {
      patchState(
        store,
        updateEntity({
          id: jobId,
          changes: () => ({ ...value })
        })
      );

      clearDirtyForIds([jobId]);

      notificationStore.refreshJobNotifications(store.entityMap()[jobId]);
    };

    const doUpdateEntities = (values: Array<Partial<TherapyJobV2>>) => {
      values.forEach((v) => {
        patchState(
          store,
          updateEntity({
            id: v.id,
            changes: () => ({ ...v })
          })
        );
      });

      clearDirtyForIds(values.map((v) => v.id));

      notificationStore.refreshAll(store.entities());
    };

    const doDeleteJob = (jobId: string) => {
      patchState(store, removeEntity(jobId));
      notificationStore.deleteNotificationsByJobId(jobId);
    };

    return {
      load: (input: LoadInput): Observable<void> => {
        loadJobs(input);
        return loaded.asObservable();
      },
      patchJob(value: Partial<TherapyJobV2>, jobId: string) {
        doPatchJob(value, jobId);
      },
      /**
       * Applies a live WebSocket update, deferring it until any in-flight reload has landed so a
       * stale snapshot cannot overwrite it.
       */
      applyWsPatch(value: Partial<TherapyJobV2>, jobId: string) {
        if (loadInFlight) {
          pendingWsMutations.push(() => doPatchJob(value, jobId));
          return;
        }
        doPatchJob(value, jobId);
      },
      updateEntities(values: Array<Partial<TherapyJobV2>>) {
        doUpdateEntities(values);
      },
      /** Batched counterpart of applyWsPatch, for the buffered JOB_UPDATE collector. */
      applyWsUpdates(values: Array<Partial<TherapyJobV2>>) {
        if (loadInFlight) {
          pendingWsMutations.push(() => doUpdateEntities(values));
          return;
        }
        doUpdateEntities(values);
      },
      /** Applies a JOB_DELETED, deferred like applyWsPatch so a stale snapshot cannot bring the job back. */
      deleteJob(jobId: string) {
        if (loadInFlight) {
          pendingWsMutations.push(() => doDeleteJob(jobId));
          return;
        }
        doDeleteJob(jobId);
      },
      updateFilter(filter: Filter) {
        const previousRange: PlannedTimeRange | null = getPlannedTimeQuery(store.filter())?.value ?? null;
        const nextRange: PlannedTimeRange | null = getPlannedTimeQuery(filter)?.value ?? null;

        patchState(store, { filter });

        // plannedTime is resolved on the backend, so a range change requires a reload.
        // Other filters are applied client-side and do not hit the server.
        if (JSON.stringify(previousRange) !== JSON.stringify(nextRange)) {
          loadJobs({ id: 'plannedTime', showLoader: true });
        }
      },
      setLoading(loading: boolean) {
        patchState(store, { loading });
      },
      resetFilters() {
        patchState(store, { filter: { queries: [] } });
      },
      setAnnotationDirty(id: string, dirty: boolean) {
        const current = store.dirtyAnnotationIds();
        const next = dirty ? Array.from(new Set([...current, id])) : current.filter((existingId) => existingId !== id);

        patchState(store, { dirtyAnnotationIds: next });
      },
      clearDirtyAnnotations() {
        patchState(store, { dirtyAnnotationIds: [] });
      },
      setConnectionType(connectionType: ConnectionType) {
        patchState(store, { connectionType });
      },
      setProcessingTherapyJob(jobId: string | null) {
        patchState(store, { processingTherapyJob: jobId });
      },
      clearProcessingTherapyJob() {
        patchState(store, { processingTherapyJob: null });
      }
    };
  }),
  withComputed(({ filter, entities, entityMap, dirtyAnnotationIds }, uiStore = inject(UiStore)) => ({
    unsavedChangesInDashboard: computed<boolean>(() => dirtyAnnotationIds().some((id) => !!entityMap()[id])),
    medications: computed(() => {
      return Object.values(
        entities().reduce((map, tj) => {
          const medId = getIdFromText(tj?.medicationDisplay || '');

          map[medId] = { id: medId, display: tj?.medicationDisplay || '' };
          return map;
        }, {})
      ).filter((c) => !!c);
    }),
    carriers: computed(() => {
      return Object.values(
        entities().reduce((map, tj) => {
          const carrier: string = tj?.carrierEdited || tj?.carrierPrescribed;
          const carrierId = getIdFromText(carrier);

          map[carrierId] = { id: carrierId, description: carrier };
          return map;
        }, {})
      ).filter((c) => !!c);
    }),
    // Patient and physician data travels with every therapy job, so the filter options are
    // derived from the loaded jobs instead of the unit-wide patient/physician lists.
    patients: computed<Array<Patient>>(() => {
      return Object.values(
        entities().reduce(
          (map, tj) => {
            if (tj?.patient?.id) {
              map[tj.patient.id] = tj.patient;
            }
            return map;
          },
          {} as { [key: string]: Patient }
        )
      );
    }),
    physicians: computed<Array<Employee>>(() => {
      return Object.values(
        entities().reduce(
          (map, tj) => {
            if (tj?.physician?.id) {
              map[tj.physician.id] = tj.physician;
            }
            return map;
          },
          {} as { [key: string]: Employee }
        )
      );
    }),
    rooms: computed(() => {
      return Array.from(
        new Set(
          entities()
            .map((tj) => tj?.patient?.careUnit?.room)
            .filter((r) => !!r)
        )
      ).map((r) => ({ id: r }));
    }),
    filtered: computed(() => {
      let filtered = entities();

      filter.queries().forEach((f) => {
        switch (f.on) {
          case 'medicine':
            if (!f.bindValue?.length) {
              return;
            }

            filtered = filtered.filter((tj) => f.bindValue.includes(tj.medicationDisplay));
            break;

          case 'patient':
            if (!f.bindValue?.length) {
              return;
            }

            filtered = filtered.filter((tj) => f.bindValue.includes(tj?.patientId));
            break;

          case 'physician':
            if (!f.bindValue?.length) {
              return;
            }

            filtered = filtered.filter((tj) => f.bindValue.includes(tj?.physicianId));
            break;

          case 'carrier':
            if (!f.bindValue?.length) {
              return;
            }

            filtered = filtered.filter((tj) => f.bindValue.includes(tj?.carrierEdited || tj?.carrierPrescribed));
            break;

          case 'prepared':
            filtered = filtered.filter((tj) => {
              // If bindValue is false (not null!), filter out jobs that are not prepared
              if (f.bindValue === false) {
                return !tj?.prepared && tj?.status === TherapyJobStatus.PRESCRIBED;
              }

              return !!tj?.prepared && tj?.status === TherapyJobStatus.PRESCRIBED;
            });
            break;

          case 'room':
            if (!f.bindValue?.length) {
              return;
            }

            filtered = filtered.filter((tj) => f.bindValue.includes(tj?.patient?.careUnit?.room));
            break;

          case 'search': {
            const words = splitWords(f.bindValue.toLowerCase());
            filtered = filtered.filter((tj) => {
              const fullName = `${tj?.patient?.firstName} ${tj?.patient?.lastName}`;
              const fullMedicine = tj?.medicationDisplay || '';
              const isInTheName = words.every(
                (w) => fullName?.toLowerCase().includes(w) || fullMedicine?.toLowerCase().includes(w)
              );

              return isInTheName;
            });
            break;
          }
        }
      });

      return filtered;
    }),
    allFiltersApplied: computed<number>(() => {
      return filter.queries().reduce((acc, f) => acc + countAppliedFilters(f), 0);
    }),
    activeFiltersInSideBar: computed<number>(() => {
      return filter.queries().reduce((acc, f) => {
        // On desktop the quick filters are always visible so we do not count them
        if (!uiStore.screenSmallerThan1050() && filterConfigMap[f.on]?.showAsQuickFilter) {
          return acc;
        }

        return acc + countAppliedFilters(f);
      }, 0);
    })
  }))
);

export type TherapyJobStore = InstanceType<typeof TherapyJobStore>;
